<template>
    <div class="congress-schedule-event-card">
        <!-- Collapsible Header -->
        <div class="congress-schedule-event-card-header" @click="toggleExpanded">
            <div class="congress-schedule-event-card-header-inner">
                <div class="congress-schedule-event-card-header-inner-left">
                    <icon
                        icon="phosphor/regular/arrows-out-cardinal"
                        class="congress-schedule-event-card-header-move cursor-grab"
                    ></icon>
                </div>
                <div class="congress-schedule-event-card-header-label">
                    <span class="font-medium">
                        {{ accommodation.name || lang('congresses::congresses.accommodations.untitled') }}
                    </span>
                    <span v-if="accommodation.stars" class="ml-2">
                        <Icon v-for="n in accommodation.stars" :key="n" icon="phosphor/regular/star" class="size-3" />
                    </span>
                </div>
                <div class="congress-schedule-event-card-header-actions">
                    <Button type="button" size="sm" color="danger" variant="inline" @click.stop="$emit('remove')">
                        <span class="action-icon">
                            <Icon icon="phosphor/regular/trash"></Icon>
                        </span>
                        <span class="action-label">
                            {{ lang('congresses::congresses.accommodations.remove') }}
                        </span>
                    </Button>
                    <icon
                        icon="hero/outline/chevron-down"
                        class="congress-schedule-event-card-header-actions-icon"
                        :class="{ 'rotate-180': expanded }"
                    ></icon>
                </div>
            </div>
        </div>

        <!-- Expanded Content -->
        <div v-show="expanded" class="event-content mt-2 space-y-2">
            <!-- Tabs -->
            <div class="px-5">
                <Tabs :tabs="tabs" class="mb-3" />
            </div>

            <!-- Tab 1: Základní info -->
            <div v-show="activeTab === 'basic'" class="space-y-2 px-5">
                <Field
                    v-model="accommodation.is_display_only"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.accommodations.is_display_only'),
                        slug: `accommodations.${index}.is_display_only`,
                        type: 'checkbox',
                        adminComponent: 'AdminCheckbox',
                    }"
                >
                    <template #help>
                        <small>
                            {{ lang('congresses::congresses.accommodations.is_display_only_help') }}
                        </small>
                    </template>
                </Field>

                <Field
                    v-model="accommodation.name"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.accommodations.name'),
                        slug: `accommodations.${index}.name`,
                        type: 'text',
                        adminComponent: 'AdminText',
                        required: true,
                    }"
                />

                <Field
                    v-model="accommodation.stars"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.accommodations.stars'),
                        slug: `accommodations.${index}.stars`,
                        type: 'number',
                        adminComponent: 'AdminNumber',
                    }"
                />

                <Field
                    v-model="accommodation.description"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.accommodations.description'),
                        slug: `accommodations.${index}.description`,
                        type: 'wysiwyg',
                        adminComponent: 'AdminWYSIWYG',
                        options: {
                            text: ['normal', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
                            fonts: true,
                            font_sizes: true,
                        },
                    }"
                />

                <!-- Contact Info Group -->
                <div class="grid grid-cols-2 gap-5">
                    <Field
                        v-model="accommodation.info.email"
                        size="sm"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.accommodations.email'),
                            slug: `accommodations.${index}.info.email`,
                            type: 'email',
                            adminComponent: 'AdminEmail',
                        }"
                    />
                    <Field
                        v-model="accommodation.info.phone"
                        size="sm"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.accommodations.phone'),
                            slug: `accommodations.${index}.info.phone`,
                            type: 'tel',
                            adminComponent: 'AdminTel',
                        }"
                    />
                    <Field
                        v-model="accommodation.info.web"
                        size="sm"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.accommodations.web'),
                            slug: `accommodations.${index}.info.web`,
                            type: 'text',
                            adminComponent: 'AdminText',
                        }"
                    >
                        <template #help>
                            <small>
                                {{ lang('congresses::congresses.accommodations.web_help') }}
                            </small>
                        </template>
                    </Field>
                    <Field
                        v-model="accommodation.info.distance"
                        size="sm"
                        label-position="top"
                        :field="{
                            name: lang('congresses::congresses.accommodations.distance'),
                            slug: `accommodations.${index}.info.distance`,
                            type: 'text',
                            adminComponent: 'AdminText',
                        }"
                    />
                </div>

                <Field
                    v-model="accommodation.info.address"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.accommodations.address'),
                        slug: `accommodations.${index}.info.address`,
                        type: 'text',
                        adminComponent: 'AdminText',
                    }"
                />

                <Field
                    v-model="accommodation.info.maps_link"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.accommodations.maps_link'),
                        slug: `accommodations.${index}.info.maps_link`,
                        type: 'text',
                        adminComponent: 'AdminText',
                    }"
                />

                <!-- Ticket Restrictions -->
                <Field
                    v-if="ticketOptions.length > 0"
                    v-model="accommodation.ticket_ids"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.accommodations.ticket_restrictions'),
                        slug: `accommodations.${index}.ticket_ids`,
                        type: 'select',
                        adminComponent: 'AdminSelect',
                        portal: true,
                        multiple: true,
                        options: ticketOptions,
                        placeholder: lang('congresses::congresses.accommodations.no_ticket_restriction'),
                        help: lang('congresses::congresses.accommodations.ticket_restrictions_help'),
                    }"
                >
                    <template #help>
                        <small>
                            {{ lang('congresses::congresses.accommodations.ticket_restrictions_help') }}
                        </small>
                    </template>
                </Field>

                <Field
                    v-if="ticketOptions.length > 0"
                    v-model="accommodation.forbidden_ticket_ids"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.accommodations.forbidden_ticket_restrictions'),
                        slug: `accommodations.${index}.forbidden_ticket_ids`,
                        type: 'select',
                        adminComponent: 'AdminSelect',
                        portal: true,
                        multiple: true,
                        options: ticketOptions,
                        placeholder: lang('congresses::congresses.accommodations.no_forbidden_ticket_restriction'),
                    }"
                >
                    <template #help>
                        <small>
                            {{ lang('congresses::congresses.accommodations.forbidden_ticket_restrictions_help') }}
                        </small>
                    </template>
                </Field>
            </div>

            <!-- Tab 2: Fotogalerie -->
            <div v-show="activeTab === 'gallery'">
                <ImagesList v-model="accommodation.images" :accommodation-id="accommodation._id" />
            </div>

            <!-- Tab 3: Pokoje -->
            <div v-show="activeTab === 'rooms'">
                <RoomsList
                    v-model="accommodation.rooms"
                    :accommodation-id="accommodation._id"
                    :accommodation-index="index"
                    :available-tickets="availableTickets"
                    :accommodation-ticket-ids="accommodation.ticket_ids || []"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { lang } from '@/lang/composable';
import Button from '@src/Common/Components/Button.vue';
import Field from '@src/Common/Components/Field.vue';
import Tabs from '@src/Common/Components/Tabs.vue';
import ImagesList from './ImagesList.vue';
import RoomsList from './RoomsList.vue';
import type { Accommodation } from '../types';

interface Ticket {
    id: number;
    name: string;
}

const props = defineProps<{
    index: number;
    availableTickets?: Ticket[];
}>();

const ticketOptions = computed(() =>
    (props.availableTickets || []).map((t) => ({
        id: t.id,
        name: t.name,
    })),
);

defineEmits<{
    (e: 'remove'): void;
}>();

const accommodation = defineModel<Accommodation>();

// Initialize info if it doesn't exist
if (!accommodation.value.info) {
    accommodation.value.info = {};
}

const expanded = ref(false);
const activeTab = ref<'basic' | 'gallery' | 'rooms'>('basic');

const toggleExpanded = () => {
    expanded.value = !expanded.value;
};

const tabs = computed(() => [
    {
        name: lang('congresses::congresses.accommodations.tabs.basic'),
        current: () => activeTab.value === 'basic',
        click: () => {
            activeTab.value = 'basic';
        },
    },
    {
        name: `${lang('congresses::congresses.accommodations.tabs.gallery')} (${(accommodation.value?.images || []).length})`,
        current: () => activeTab.value === 'gallery',
        click: () => {
            activeTab.value = 'gallery';
        },
    },
    {
        name: `${lang('congresses::congresses.accommodations.tabs.rooms')} (${(accommodation.value?.rooms || []).length})`,
        current: () => activeTab.value === 'rooms',
        click: () => {
            activeTab.value = 'rooms';
        },
    },
]);
</script>
